/**
 * Streaming (SSE) chat transport (ADR 0006/0007): the `ChatboxStreamFactory` backed by the SHARED
 * `ChatTransport` (`ai-teammate/utils/chat-transport.js`) hitting the real pi-server SSE endpoints
 * (`POST .../threads/stream`, `.../threads/:threadId/stream`, and resume via active-stream). Same
 * factory shape as the buffered path, so the controller swaps transports behind the adapter with no
 * surface change; `request.signal` threads through to the fetch so Stop aborts the in-flight turn.
 */

import type { AiSdkUIMessageChunk } from "@mui/x-chat"
import type { ChatMessage } from "@mui/x-chat-headless"
import { assembleAssistantMessage } from "./adapter"
import type { ChatboxStreamFactory, ChatboxUIMessageStream, ProgressFlushScheduler } from "./adapter"
import { messageText } from "./buffered-transport"
import { describeChatApiError } from "./chat-api-errors"
import { type FinishMetadataSink, normalizeChunkStream } from "./normalize-chunks"

/** The subset of the shared `ChatTransport` the chatbox surface drives (the JS class is untyped). */
export interface StreamingChatTransport {
  sendMessages(args: { chatId: string | null, body: unknown, abortSignal: AbortSignal }): ChatboxUIMessageStream
  reconnectToStream(args: { chatId: string, abortSignal: AbortSignal, tolerateNonTerminalEnd?: boolean, fromStart?: boolean }): Promise<ChatboxUIMessageStream | null>
  /**
   * Server-side stop: DELETE the (chatId ?? active) stream so op-summarize halts generation (APP-3394).
   * Optional — the current shared transport has no such method; it arrives with the drawer's stop (#4141),
   * so callers optional-chain it and the chatbox no-ops until then.
   */
  cancelStream?(chatId: string | null): Promise<void>
}

/** Opens the SSE turn for the current send context: start (no chatId) or continue (chatId), aborting on `signal`. */
export type OpenStreamingTurn = (userText: string, signal: AbortSignal) => ChatboxUIMessageStream

/** Build the streaming `ChatboxStreamFactory`; the caller closes over the send context (thread id, body), mirroring the buffered factory. */
export function createStreamingChatStream({ openStreamingTurn }: { openStreamingTurn: OpenStreamingTurn }): ChatboxStreamFactory {
  return (request) => openStreamingTurn(messageText(request.message), request.signal)
}

/** The active-turn registration the controller supplies so a factory turn (composer OR programmatic) is observable + stoppable regardless of initiator. */
export interface ActiveTurnHooks {
  /** Register a fresh controller as the active turn; its `signal` is what the transport aborts on. */
  beginTurn: () => AbortController
  /** Clear the active turn once its stream settles (only if still the registered one). */
  endTurn: (controller: AbortController) => void
}

function errorChunk(message: string): AiSdkUIMessageChunk {
  return { type: "error", errorText: message }
}

function calmErrorStream(signal: AbortSignal, error: unknown): ChatboxUIMessageStream {
  return new ReadableStream<AiSdkUIMessageChunk>({
    start(controller) {
      // A signal-abort is expected teardown, not an error to render.
      if (!signal.aborted) controller.enqueue(errorChunk(describeChatApiError(error).message))
      controller.close()
    },
  })
}

/** Pass the source through, settling the turn on close/error/cancel; a transport-level rejection becomes one calm errorChunk (a signal-abort stays silent). */
function guardedStream(source: ChatboxUIMessageStream, signal: AbortSignal, settle: () => void): ChatboxUIMessageStream {
  const reader = source.getReader()
  let settled = false
  const done = () => {
    if (settled) return
    settled = true
    settle()
  }
  return new ReadableStream<AiSdkUIMessageChunk>({
    async pull(controller) {
      try {
        const { value, done: finished } = await reader.read()
        if (finished) {
          controller.close()
          done()
          return
        }
        // Aborted (Stop or nav-away) while this read was pending: drop the buffered chunk instead of emitting
        // it — a finish sitting in the fetch buffer must not reach the finish tap and adopt/hijack the new
        // view. Checked after the read (like the buffered transport) so both paths settle identically.
        if (signal.aborted) {
          controller.close()
          done()
          return
        }
        controller.enqueue(value)
      } catch (error) {
        done()
        if (signal.aborted) {
          controller.close()
          return
        }
        controller.enqueue(errorChunk(describeChatApiError(error).message))
        controller.close()
      }
    },
    cancel(reason) {
      done()
      return reader.cancel(reason)
    },
  })
}

/**
 * Wrap any factory so each turn (a) registers a controller in the controller-held ref — linked to the
 * caller's request signal so composer Stop still aborts cleanly — making the turn observable/stoppable
 * regardless of initiator, and (b) surfaces transport-level failures as a single calm errorChunk to match
 * the buffered path (the streaming transport otherwise rejects into MUI's generic error).
 */
export function withActiveTurn(factory: ChatboxStreamFactory, { beginTurn, endTurn }: ActiveTurnHooks): ChatboxStreamFactory {
  return async (request) => {
    const controller = beginTurn()
    if (request.signal.aborted) controller.abort()
    else request.signal.addEventListener("abort", () => controller.abort(), { once: true })
    const settle = () => endTurn(controller)
    let source: ChatboxUIMessageStream
    try {
      source = await factory({ ...request, signal: controller.signal })
    } catch (error) {
      settle()
      return calmErrorStream(controller.signal, error)
    }
    return guardedStream(source, controller.signal, settle)
  }
}

/** Taps for a resume reattach: finish-metadata (turn ids) and per-frame progress (live-token render, APP-3376). */
export interface ReattachStreamOptions {
  onFinishMetadata?: FinishMetadataSink
  /** Coalesced partial snapshots as the replay arrives — wire it to render the resumed turn token-by-token. */
  onProgress?: (partial: ChatMessage) => void
  /** Coalescing scheduler forwarded to the assembler; defaults to requestAnimationFrame. Injectable for tests. */
  scheduleProgressFlush?: ProgressFlushScheduler
}

/**
 * Attach-on-select resume: reconnect to a thread's in-flight stream (`active-stream` + from-start replay,
 * per pi-server ADR 0006) and assemble the reattached turn. Returns `null` when the thread has no active
 * stream. The finish tap captures the turn ids so the assembled turn stays consistent with the live-send path;
 * `onProgress` emits coalesced partials so the reattached turn renders token-by-token (APP-3376).
 * `tolerateNonTerminalEnd`: a replay that drops before its terminal event still yields what it assembled — a
 * partial answer under the fallback id (no synthetic finish; the drop closes the open text part in the transport).
 * `fromStart`: replay the WHOLE active stream, not from the tail cursor — Kelly's controlled view is empty on
 * attach (it wipes live turns on nav-away), so resuming from the tail would drop every interstitial narrative and
 * tool call buffered before the attach (APP-3376).
 */
export async function reattachActiveStream(
  transport: StreamingChatTransport,
  chatId: string,
  signal: AbortSignal,
  fallbackMessageId: string,
  options?: ReattachStreamOptions,
): Promise<ChatMessage | null> {
  const source = await transport.reconnectToStream({ chatId, abortSignal: signal, tolerateNonTerminalEnd: true, fromStart: true })
  if (source == null) return null
  const { onFinishMetadata, onProgress, scheduleProgressFlush } = options ?? {}
  return assembleAssistantMessage(normalizeChunkStream(source, onFinishMetadata), fallbackMessageId, { onProgress, scheduleProgressFlush })
}
